import { useState, useMemo } from "react";
import axios from "axios";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { Calendar, Clock, Search, MapPin, Loader2, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

const CONFIG_URL = "/config.json";

async function getApiBase() {
  const res = await fetch(CONFIG_URL, { cache: "no-store" });
  if (!res.ok) throw new Error("No se pudo cargar config.json");
  const cfg = await res.json();
  if (!cfg.backendBase) throw new Error("config.json sin backendBase");
  return `${cfg.backendBase}/api`;
}


// Generate hours for select (6:00 - 23:00)
const HOURS = Array.from({ length: 18 }, (_, i) => i + 6);

const PadelFinder = () => {
  const [date, setDate] = useState(new Date());
  const [hour, setHour] = useState("18");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [openCourts, setOpenCourts] = useState({});

  const searchAvailability = async () => {
    if (!date) {
      toast.error("Selecciona una fecha");
      return;
    }

    setLoading(true);
    setHasSearched(true);
    setOpenCourts({});

    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const api = await getApiBase();
      const response = await axios.get(`${api}/availability`, {
        params: {
          date: dateStr,
          start_hour: parseInt(hour)
        }
      });
      
      setResults(response.data);
      
      if (response.data.length === 0) {
        toast.info("No se encontraron pistas disponibles");
      } else {
        toast.success(`${response.data.length} slots encontrados`);
      }
    } catch (error) {
      console.error("Error fetching availability:", error);
      toast.error("Error al buscar disponibilidad");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Group results by price, then by court
  const groupedResults = useMemo(() => {
    if (!results.length) return [];

    // Group by price
    const priceGroups = {};
    
    results.forEach((result) => {
      const price = result.slot.price_value;
      const priceStr = result.slot.price;
      
      if (!priceGroups[price]) {
        priceGroups[price] = {
          price: price,
          priceStr: priceStr,
          courts: {}
        };
      }
      
      // Create unique court key (club + resource_id)
      const courtKey = `${result.club_name}|${result.resource_id}`;
      
      if (!priceGroups[price].courts[courtKey]) {
        priceGroups[price].courts[courtKey] = {
          club_name: result.club_name,
          club_slug: result.club_slug,
          club_address: result.club_address,
          resource_id: result.resource_id,
          times: []
        };
      }
      
      priceGroups[price].courts[courtKey].times.push({
        start_time: result.slot.start_time,
        duration: result.slot.duration
      });
    });

    // Convert to array and sort by price
    return Object.values(priceGroups)
      .sort((a, b) => a.price - b.price)
      .map(group => ({
        ...group,
        courts: Object.values(group.courts).map(court => ({
          ...court,
          times: court.times.sort((a, b) => a.start_time.localeCompare(b.start_time))
        }))
      }));
  }, [results]);

  const formatPrice = (priceStr) => {
    const parts = priceStr.split(" ");
    return parts[0];
  };

  const formatTime = (timeStr) => {
    return timeStr.substring(0, 5);
  };

  const toggleCourt = (priceIdx, courtIdx) => {
    const key = `${priceIdx}-${courtIdx}`;
    setOpenCourts(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const totalCourts = useMemo(() => {
    return groupedResults.reduce((acc, group) => acc + group.courts.length, 0);
  }, [groupedResults]);

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="bg-text font-heading top-1/4 -left-10 rotate-[-10deg]">PADEL</div>
      
      {/* Main container */}
      <div className="max-w-lg mx-auto min-h-screen flex flex-col relative z-10">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-md border-b border-border p-6">
          <h1 
            data-testid="app-title"
            className="font-heading font-black text-3xl sm:text-4xl tracking-tighter uppercase italic text-foreground glitch-text cursor-default"
          >
            PADEL PINF <span className="text-primary">FINDER</span>
          </h1>
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mt-1">
            Terrassa
          </p>
        </header>

        {/* Search Section */}
        <section className="p-6 space-y-4" data-testid="search-section">
          {/* Date Picker */}
          <div className="space-y-2">
            <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Calendar className="w-3 h-3" />
              Fecha
            </label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  data-testid="date-picker"
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-mono bg-zinc-900/50 border-zinc-800 hover:bg-zinc-800 hover:border-primary rounded-none h-12",
                    !date && "text-muted-foreground"
                  )}
                >
                  <Calendar className="mr-2 h-4 w-4 text-primary" />
                  {date ? (
                    format(date, "EEEE, d 'de' MMMM", { locale: es })
                  ) : (
                    <span>Seleccionar fecha</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-zinc-950 border-zinc-800 rounded-none" align="start">
                <CalendarComponent
                  mode="single"
                  selected={date}
                  onSelect={(newDate) => {
                    setDate(newDate);
                    setCalendarOpen(false);
                  }}
                  disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                  initialFocus
                  locale={es}
                  className="rounded-none"
                  classNames={{
                    day_selected: "bg-primary text-black hover:bg-primary hover:text-black focus:bg-primary focus:text-black rounded-none font-bold",
                    day_today: "bg-zinc-800 text-white rounded-none",
                    day: "rounded-none hover:bg-zinc-800 font-mono text-sm",
                    head_cell: "text-muted-foreground font-mono text-xs uppercase",
                    caption: "font-heading font-bold",
                    nav_button: "border-zinc-700 hover:bg-zinc-800 rounded-none"
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Hour Select */}
          <div className="space-y-2">
            <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Clock className="w-3 h-3" />
              Hora desde
            </label>
            <Select value={hour} onValueChange={setHour}>
              <SelectTrigger 
                data-testid="hour-select"
                className="w-full bg-zinc-900/50 border-zinc-800 hover:border-primary rounded-none h-12 font-mono"
              >
                <SelectValue placeholder="Seleccionar hora" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 rounded-none max-h-60">
                {HOURS.map((h) => (
                  <SelectItem 
                    key={h} 
                    value={h.toString()}
                    className="font-mono rounded-none hover:bg-zinc-800 focus:bg-zinc-800"
                  >
                    {h.toString().padStart(2, "0")}:00
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Search Button */}
          <Button
            data-testid="search-btn"
            onClick={searchAvailability}
            disabled={loading}
            className="w-full h-14 rounded-none uppercase font-bold tracking-wider bg-primary text-black hover:bg-neon-lime border-0 transition-all duration-300 shadow-[0_0_15px_rgba(204,255,0,0.2)] hover:shadow-[0_0_30px_rgba(204,255,0,0.5)] text-lg"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Buscando...
              </>
            ) : (
              <>
                <Search className="mr-2 h-5 w-5" />
                Buscar Pistas
              </>
            )}
          </Button>
        </section>

        {/* Results Section */}
        <section className="flex-1 p-6 pt-0" data-testid="results-section">
          {hasSearched && groupedResults.length > 0 && (
            <div className="mb-4">
              <h2 className="font-heading font-bold text-lg text-foreground">
                <span className="text-primary">{totalCourts}</span> pistas en{" "}
                <span className="text-primary">{groupedResults.length}</span> precios
              </h2>
              <p className="font-mono text-xs text-muted-foreground uppercase tracking-wider">
                Agrupadas por precio
              </p>
            </div>
          )}

          {/* Price Groups */}
          <div className="space-y-4">
            {groupedResults.map((priceGroup, priceIdx) => (
              <div
                key={priceGroup.price}
                data-testid={`price-group-${priceIdx}`}
                className="fade-in-up"
                style={{ animationDelay: `${priceIdx * 0.1}s` }}
              >
                {/* Price Header */}
                <div className="bg-primary/10 border border-primary/30 p-3 mb-2">
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-bold text-2xl text-primary">
                      {formatPrice(priceGroup.priceStr)} <span className="text-sm text-primary/70">EUR</span>
                    </span>
                    <span className="font-mono text-xs text-muted-foreground">
                      {priceGroup.courts.length} {priceGroup.courts.length === 1 ? 'pista' : 'pistas'}
                    </span>
                  </div>
                </div>

                {/* Courts in this price group */}
                <div className="space-y-2 pl-2 border-l-2 border-primary/20">
                  {priceGroup.courts.map((court, courtIdx) => {
                    const isOpen = openCourts[`${priceIdx}-${courtIdx}`];
                    
                    return (
                      <Collapsible
                        key={`${court.club_name}-${court.resource_id}`}
                        open={isOpen}
                        onOpenChange={() => toggleCourt(priceIdx, courtIdx)}
                      >
                        <div 
                          className="bg-card/50 backdrop-blur-md border border-border hover:bg-white/5 transition-colors"
                          data-testid={`court-card-${priceIdx}-${courtIdx}`}
                        >
                          <CollapsibleTrigger asChild>
                            <button className="w-full p-3 flex items-center justify-between text-left">
                              <div className="flex-1 min-w-0">
                                <h3 className="font-heading font-bold text-sm text-foreground truncate">
                                  {court.club_name}
                                </h3>
                                <div className="flex items-center gap-2 mt-1">
                                  <MapPin className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                  <span className="text-xs text-muted-foreground truncate">
                                    {court.club_address}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 ml-2">
                                <span className="font-mono text-xs text-primary bg-primary/10 px-2 py-1">
                                  {court.times.length} {court.times.length === 1 ? 'hora' : 'horas'}
                                </span>
                                <ChevronDown 
                                  className={cn(
                                    "w-4 h-4 text-muted-foreground transition-transform",
                                    isOpen && "rotate-180"
                                  )} 
                                />
                              </div>
                            </button>
                          </CollapsibleTrigger>

                          <CollapsibleContent>
                            <div className="px-3 pb-3 border-t border-border/50">
                              <div className="flex flex-wrap gap-2 mt-3">
                                {court.times.map((time, timeIdx) => (
                                  <div
                                    key={`${time.start_time}-${timeIdx}`}
                                    className="bg-zinc-900 border border-zinc-800 px-3 py-1.5 flex items-center gap-2"
                                    data-testid={`time-slot-${priceIdx}-${courtIdx}-${timeIdx}`}
                                  >
                                    <Clock className="w-3 h-3 text-primary" />
                                    <span className="font-mono text-sm text-foreground">
                                      {formatTime(time.start_time)}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      {time.duration}min
                                    </span>
                                  </div>
                                ))}
                              </div>
                              <a
                                href={`https://playtomic.io/clubs/${court.club_slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block mt-3 font-mono text-xs text-muted-foreground hover:text-primary transition-colors uppercase tracking-wider"
                                data-testid={`playtomic-link-${priceIdx}-${courtIdx}`}
                              >
                                Ver en Playtomic →
                              </a>
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Empty state */}
          {hasSearched && results.length === 0 && !loading && (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-none bg-zinc-900 flex items-center justify-center">
                <Search className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground font-body">
                No hay pistas disponibles para esta fecha y hora
              </p>
              <p className="text-muted-foreground/60 font-mono text-xs mt-2">
                Prueba con otra fecha u hora
              </p>
            </div>
          )}

          {/* Initial state */}
          {!hasSearched && (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-none bg-zinc-900 flex items-center justify-center border border-zinc-800">
                <Search className="w-8 h-8 text-primary" />
              </div>
              <p className="text-foreground font-body">
                Selecciona fecha y hora para buscar
              </p>
              <p className="text-muted-foreground font-mono text-xs mt-2 uppercase tracking-wider">
                4 clubes en Terrassa
              </p>
            </div>
          )}
        </section>

        {/* Footer */}
        <footer className="p-6 border-t border-border">
          <p className="font-mono text-xs text-center text-muted-foreground uppercase tracking-wider">
            Datos de <span className="text-primary">Playtomic</span>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default PadelFinder;
